"use client";

import React, { useState } from "react";
import Breadcrumb from "@/components/Common/Breadcrumb";
import { useTranslations } from "next-intl";
import { getImagePath } from "@/lib/utils";
import { useLocale } from "next-intl";
import { formatNumberByLocale } from "@/utils/numberFormatter";

const Page = () => {
  const t = useTranslations("support");
  const locale = useLocale();

  const [activeTab, setActiveTab] = useState(0);

  // TAB LABELS
  const tabs = [
    t("partners_label"),
    t("producers_label"),
    t("villagers_label"),
  ];

  // SUPPORT FAQ DATA (all translation keys only)
  // First tab: "Own & Earn Support" in en/mr/hi (7 items), "For Partners" elsewhere (5).
  const partnerSupport: { q: string; a: string }[] = [];
  for (let i = 1; t.has(`ps_q${i}`); i++) {
    partnerSupport.push({ q: t(`ps_q${i}`), a: t(`ps_a${i}`) });
  }

  const producerSupport = [
    { q: t("pr_q1"), a: t("pr_a1") },
    { q: t("pr_q2"), a: t("pr_a2") },
    { q: t("pr_q3"), a: t("pr_a3") },
    { q: t("pr_q4"), a: t("pr_a4") },
    { q: t("pr_q5"), a: t("pr_a5") },
  ];

  const villageSupport = [
    { q: t("v_q1"), a: t("v_a1") },
    { q: t("v_q2"), a: t("v_a2") },
    { q: t("v_q3"), a: t("v_a3") },
    { q: t("v_q4"), a: t("v_a4") },
    { q: t("v_q5"), a: t("v_a5") },
  ];

  const tabFaqs = [partnerSupport, producerSupport, villageSupport];

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={t("pageName")}
        description=""
        bgImages={[getImagePath("/images/breadcrumb/howItWorks.webp")]}
        bgIndex={0}
      />

      <div className="container px-4 md:px-6 py-10 w-full">
        {/* ---------- LEFT SECTION: DEVICE INFO ----------- */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-16">
          <div className="w-full md:w-7/12 mx-auto mb-10 px-2">
            {/* TITLE */}
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-[#003399]">
              {t("title")}
            </h2>

            <p className="text-lg text-[#575757] mb-8">{t("subtitle")}</p>

            {/* DEVICE SPECIFICATIONS */}
            <div className="mb-6">
              <p className="text-xl font-semibold text-[#0099CC] mb-2">
                {t("device_spec_title")}
              </p>
              <ul className="list-disc list-inside text-lg ml-4">
                <li>
                  <span className="text-[#575757]">{t("device_os")}</span>
                </li>
                <li>
                  <span className="text-[#575757]">{t("device_gps")}</span>
                </li>
                <li>
                  <span className="text-[#575757]">{t("device_drm")}</span>
                </li>
                <li>
                  <span className="text-[#575757]">{t("device_video")}</span>
                </li>
                <li>
                  <span className="text-[#575757]">{t("device_connect")}</span>
                </li>
              </ul>
            </div>

            {/* SETUP INSTRUCTIONS */}
            <div className="mb-6">
              <p className="text-xl font-semibold mb-2 text-[#0099CC]">
                {t("setup_title")}
              </p>
              <p className="text-lg text-[#575757]">{t("setup_desc")}</p>
            </div>

            {/* TROUBLESHOOTING */}
            <div>
              <p className="text-xl font-semibold text-[#0099CC] mb-2">
                {t("trouble_title")}
              </p>
              <ul className="list-disc text-[#575757] list-inside text-lg ml-4">
                <li>{t("trouble_1")}</li>
                <li>{t("trouble_2")}</li>
                <li>{t("trouble_3")}</li>
              </ul>
            </div>
          </div>

          {/* --------- RIGHT SECTION IMAGE --------- */}
          <div className="w-6/12 mx-auto mb-10 px-2">
            <img
              src={getImagePath("/images/support/dongle-specs.jpeg")}
              alt="Device"
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>

        {/* ---------- FAQ TABS ---------- */}
        <div className="flex flex-col md:flex-row text-gray-700 text-lg leading-relaxed mb-8">
          {tabs.map((tab, idx) => (
            <button
              key={idx}
              onClick={() => setActiveTab(idx)}
              className={`bg-transparent w-auto md:w-full border h-12 md:h-16 mb-5 font-semibold text-lg transition-colors duration-200
              ${
                activeTab === idx
                  ? "text-[#003399] border-[#003399]"
                  : "text-[#C9C9C9] border-gray-300"
              }`}
            >
              <h2 className="text-lg font-semibold">{tab}</h2>
            </button>
          ))}
        </div>

        {/* ---------- FAQ LIST ---------- */}
        <div className="w-full mt-8">
          <div className="space-y-6">
            {tabFaqs[activeTab].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                {/* Multi-language Number Formatting */}
                <span className="text-lg font-bold ">
                  {formatNumberByLocale(locale, i + 1)}.
                </span>

                <div>
                  <span className="font-bold mb-2 text-lg">{item.q}</span>
                  <p>{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ---------- CONTACT BOX ---------- */}
        <div className="mt-12 border border-[#C9C9C9] rounded-tl-3xl rounded-br-3xl p-6 max-w-2xl">
          <p className="text-lg text-black font-bold mb-2">
            {t("contact_title")}
          </p>
          <ul className="text-lg font-bold space-y-1">
            <li>{t("contact_email")}</li>
            <li>{t("contact_hours")}</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Page;
